import { browser, dev } from '$app/environment';
import log from '$lib/utils/log';
import { getPrice, type StripePriceData } from '$lib/utils/stripe';

export class ReactiveProduct {
	#lookupKey: string;
	#priceData = $state<StripePriceData>();

	constructor(lookupKey: string, initial: StripePriceData) {
		this.#lookupKey = lookupKey;
		this.#priceData = initial;

		if (browser) {
			document.addEventListener('invalidate-price', this.#listener as unknown as EventListener);
		}
	}

	get price() {
		if (!this.#priceData) throw new Error('Price data not initialized');

		return this.#priceData.price;
	}

	get promotionCode() {
		return this.#priceData?.promotionCode;
	}

	#onChangeListener = $state<() => void>();

	onChange = (cb: () => void) => {
		this.#onChangeListener = cb;

		if (dev) {
			log
				.scope('ReactiveProduct')
				.debug('Subscribed to price changes', { lookupKey: this.#lookupKey });
		}
	};

	#listener = async ({ detail }: CustomEvent<{ lookupKey?: string }>) => {
		if (detail?.lookupKey && detail.lookupKey !== this.#lookupKey) return;

		this.#priceData = await getPrice(this.#lookupKey, true);

		this.#onChangeListener?.();

		if (dev) {
			log.scope('ReactiveProduct').debug('Invalidated price', { lookupKey: this.#lookupKey });
		}
	};

	unsubscribe = () => {
		if (browser) {
			document.removeEventListener('invalidate-price', this.#listener as unknown as EventListener);
		}
	};

	equals(other: ReactiveProduct) {
		return this.#lookupKey === other.#lookupKey &&
			this.price.id === other.price.id &&
			this.promotionCode?.code === other.promotionCode?.code;
	}
}

export function invalidatePrice(lookupKey: string) {
	document.dispatchEvent(new CustomEvent('invalidate-price', { detail: { lookupKey } }));
}

export function invalidateAllPrices() {
	document.dispatchEvent(new CustomEvent('invalidate-price'));
}
